// podcast-history.js — listening history + resume positions for the player.
//
// During playback we write to localStorage every ~10s (cheap, fast). On
// pause / ended / episode change / page hide we sync the merged history up
// to preferences.podcast_history so it follows the user across devices.
//
// Shape (per entry, keyed by episode id):
//   {
//     feed_id, feed_title, episode_title, audio_url, pub_date, image,
//     position_s, duration_s, last_played_at, completed
//   }

const LS_KEY = "cucina_v1:podcast_history";
const MAX_ENTRIES = 50;
const RESUME_MIN_S = 8;     // ignore tiny progress when offering "resume"
const COMPLETE_TAIL_S = 30; // if remaining ≤ 30s, treat as complete on pause/leave

function readLS() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); }
  catch { return {}; }
}

function writeLS(map) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch {}
}

function pruneToMax(map) {
  const keys = Object.keys(map);
  if (keys.length <= MAX_ENTRIES) return map;
  const sorted = keys.sort((a, b) =>
    (map[b].last_played_at || "").localeCompare(map[a].last_played_at || "")
  );
  const out = {};
  for (const k of sorted.slice(0, MAX_ENTRIES)) out[k] = map[k];
  return out;
}

// Read the merged history (preferences row + any localStorage updates that
// haven't synced yet). Local always wins on key collision because it's
// strictly newer than the last server snapshot.
export function getHistory(preferences) {
  const remote = preferences?.podcast_history || {};
  const local = readLS();
  const merged = { ...remote };
  for (const [k, v] of Object.entries(local)) {
    const r = merged[k];
    if (!r || (v.last_played_at || "") > (r.last_played_at || "")) {
      merged[k] = v;
    }
  }
  return merged;
}

// Returns a seconds value to seek to, or null if no resume should happen.
export function getResumePosition(history, episodeId) {
  const e = history?.[episodeId];
  if (!e || e.completed) return null;
  if (!e.position_s || e.position_s < RESUME_MIN_S) return null;
  // Don't try to resume into the very tail — likely effectively done.
  if (e.duration_s && (e.duration_s - e.position_s) <= COMPLETE_TAIL_S) return null;
  return e.position_s;
}

export function isCompleted(history, episodeId) {
  return !!history?.[episodeId]?.completed;
}

export function isInProgress(history, episodeId) {
  const e = history?.[episodeId];
  if (!e || e.completed) return false;
  return (e.position_s || 0) >= RESUME_MIN_S;
}

// Write a progress snapshot into localStorage. The audio events fire often,
// so this stays cheap — we batch into Supabase via syncToServer().
export function recordLocalProgress({ episode, feed, position_s, duration_s, completed = false }) {
  if (!episode?.id) return;
  const map = readLS();
  // Auto-promote to "completed" if we're effectively at the end.
  const isAtTail = duration_s > 0 && (duration_s - position_s) <= COMPLETE_TAIL_S;
  const finished = completed || isAtTail;
  map[episode.id] = {
    feed_id: feed.id,
    feed_title: feed.title || "",
    episode_title: episode.title,
    audio_url: episode.audioUrl,
    pub_date: episode.pubDate || null,
    image: episode.image || feed.image || null,
    position_s: finished ? 0 : Math.max(0, Math.floor(position_s)),
    duration_s: Math.max(0, Math.floor(duration_s || 0)),
    last_played_at: new Date().toISOString(),
    completed: finished,
  };
  writeLS(pruneToMax(map));
}

// Push localStorage history into preferences.podcast_history. Caller passes
// the store so we can read current preferences and call setPreferences. The
// remote row + local map are merged with newest-wins semantics, then capped.
export async function syncToServer(store) {
  if (!store?.setPreferences) return;
  const local = readLS();
  if (Object.keys(local).length === 0) return;
  const remote = store.preferences?.podcast_history || {};
  const merged = { ...remote };
  for (const [k, v] of Object.entries(local)) {
    const r = merged[k];
    if (!r || (v.last_played_at || "") > (r.last_played_at || "")) {
      merged[k] = v;
    }
  }
  const capped = pruneToMax(merged);
  try {
    await store.setPreferences({ ...store.preferences, podcast_history: capped });
  } catch (e) {
    // Non-fatal — local copy is still authoritative until next sync.
    console.warn("podcast history sync failed", e);
  }
}

// Format helper used by the UI to render "5:32 / 12:00" badges.
export function formatProgressLabel(position_s, duration_s) {
  if (!duration_s) return null;
  return `${formatTime(position_s)} / ${formatTime(duration_s)}`;
}

function formatTime(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}
